import { Link } from 'react-router-dom'
import { Music, CheckCircle, Clock, Award, Heart, Mic } from 'lucide-react'
import Hero from '../../components/Hero'
import SEO from '../../components/SEO'

export default function MusicMinistry() {
  const requirements = [
    'High school diploma or equivalent',
    'Passion for worship and ministry',
    'Basic musical ability (vocal or instrumental)',
    'Personal statement of calling',
    'Two letters of recommendation',
    'Audition or portfolio submission',
    'Completed application form'
  ]

  const courses = [
    'Biblical Foundations of Worship',
    'Spiritual Formation & Character Development',
    'Vocal Training & Technique',
    'Instrumental Music Training',
    'Music Theory & Composition',
    'Worship Leading & Team Dynamics',
    'Sound Engineering & Production',
    'Church Service Planning & Coordination',
    'Sensitivity to the Holy Spirit',
    'Cross-Cultural Worship',
    'Pastoral Ethics in Music Ministry',
    'Leadership & Discipleship'
  ]

  const careers = [
    'Worship Leader',
    'Music Minister',
    'Choir Director',
    'Church Musician',
    'Worship Team Coordinator',
    'Music Producer for Ministry',
    'Worship Pastor',
    'Music Educator in Ministry'
  ]

  return (
    <>
      <SEO 
        title="Music Ministry Training Programme - Rudovia Life College"
        description="Train to become a spiritually mature and musically equipped worship leader. Learn to lead people into genuine encounters with God through music ministry."
        keywords="music ministry training, worship leader program, church music training, Christian music education, worship ministry Zimbabwe, music ministry Africa"
      />
      <div className="pt-20">
        <Hero
          title="Music Ministry Training Programme"
          subtitle="Raising spiritually mature and musically equipped ministers"
          image="/assets/images/music_image.jpg"
          height="h-[400px]"
          primaryButton={{ text: 'Apply Now', link: '/admissions' }}
        />

        <section className="section-padding bg-white">
          <div className="container-custom">
            <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
              <div>
                <img 
                  src="/assets/images/music_image.jpg" 
                  alt="Music Ministry Training" 
                  className="rounded-2xl shadow-2xl"
                />
              </div>
              <div>
                <h2 className="heading-2 mb-6">Programme Overview</h2>
                <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                  The Music Ministry Training Programme is designed to raise spiritually mature and musically equipped ministers who use their gifts to serve God and the church with excellence. This programme goes beyond learning songs—it shapes the heart, character, and skill of those called to minister through music.
                </p>
                <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                  Students receive biblical foundations, spiritual formation, and practical music training for both singers and instrumentalists. Emphasis is placed on sensitivity to the Holy Spirit, teamwork, discipline, and effective leadership in church services and ministry settings.
                </p>
                <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                  Whether you are already serving on a worship team or sensing a call into music ministry, this programme will help you grow in confidence, skill, and spiritual depth. Our goal is to develop ministers who do not perform but lead people into genuine encounters with God through music.
                </p>
                <div className="flex items-center space-x-8 mt-8">
                  <div className="flex items-center space-x-2">
                    <Clock className="w-6 h-6 text-secondary" />
                    <div>
                      <div className="font-bold text-primary">Duration</div>
                      <div className="text-gray-600">1-2 Years</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Award className="w-6 h-6 text-secondary" />
                    <div>
                      <div className="font-bold text-primary">Qualification</div>
                      <div className="text-gray-600">Certificate/Diploma</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gray-50 rounded-xl p-8">
                <Music className="w-12 h-12 text-secondary mb-4" />
                <h3 className="text-2xl font-bold text-primary mb-4">Core Courses</h3>
                <ul className="space-y-2">
                  {courses.map((course, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{course}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-gray-50 rounded-xl p-8">
                <Mic className="w-12 h-12 text-secondary mb-4" />
                <h3 className="text-2xl font-bold text-primary mb-4">Admission Requirements</h3>
                <ul className="space-y-2">
                  {requirements.map((req, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{req}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-gray-50 rounded-xl p-8">
                <Heart className="w-12 h-12 text-secondary mb-4" />
                <h3 className="text-2xl font-bold text-primary mb-4">Ministry Opportunities</h3>
                <ul className="space-y-2">
                  {careers.map((career, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{career}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="section-padding bg-primary text-white">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">Why Study Music Ministry at Rudovia?</h2>
              <div className="grid md:grid-cols-2 gap-8 mt-12">
                <div className="text-left">
                  <h3 className="text-2xl font-bold mb-3 text-secondary">Spiritual Formation</h3>
                  <p className="text-gray-200">
                    Develop your character and spiritual maturity alongside your musical skills. Learn to minister from a place of intimacy with God.
                  </p>
                </div>
                <div className="text-left">
                  <h3 className="text-2xl font-bold mb-3 text-secondary">Practical Training</h3>
                  <p className="text-gray-200">
                    Gain hands-on experience in leading worship, coordinating teams, and serving in real ministry settings.
                  </p>
                </div>
                <div className="text-left">
                  <h3 className="text-2xl font-bold mb-3 text-secondary">Holy Spirit Sensitivity</h3>
                  <p className="text-gray-200">
                    Learn to flow with the Holy Spirit in worship, discerning His leading and creating space for genuine encounters with God.
                  </p>
                </div>
                <div className="text-left">
                  <h3 className="text-2xl font-bold mb-3 text-secondary">Excellence & Discipline</h3>
                  <p className="text-gray-200">
                    Develop musical excellence and professional discipline while maintaining a heart of worship and service.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="section-padding bg-white">
          <div className="container-custom text-center">
            <h2 className="heading-2 mb-6">Lead People Into God's Presence</h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Answer the call to music ministry. Apply to our Music Ministry Training Programme today and become a minister who leads people into genuine encounters with God through worship.
            </p>
            <Link to="/admissions" className="btn-primary">
              Apply Now
            </Link>
          </div>
        </section>
      </div>
    </>
  )
}
